import { useEffect } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import releaseNewsActions from '../../Actions/releaseNewsActions';
import commonActions from '../../Actions/commonActions';
import Card from '../../Components/Card/Card';
import { FormInput } from '../../Components/FormComponents/Input/Input';
import { Dropdown as DropdownBase } from '../../Components/FormComponents/Dropdown';
import DatePicker from '../../Components/Fundamentals/DatePicker';
import EditorBase from '../../Components/Fundamentals/Form/Editor';
import { Button as ButtonBase, ButtonOption } from '../../Components/Fundamentals/Button';
import SkynetIconButton from '../../Components/Fundamentals/IconButton/SkynetIconButton';
import Spinner, { SpinnerPosition, SpinnerSize } from '../../Components/Fundamentals/Spinner/Spinner';
import ConfirmDeleteModal from '../../Components/Modals/ConfirmDeleteModal/ConfirmDeleteModal';
import useModal from '../../Hooks/useModal';
import Modals from '../../Constants/modals';
import forms from '../../Constants/forms';
import routes from '../../Constants/routes';
import { ReleaseSeverity } from '../../Constants/releaseSeverity';
import { useAppDispatch } from '../../Store/types';
import { NEW_RELEASE_NEWS_ID, type ReleaseNewsItem, type RootState } from './ReleaseNews.types';

interface ButtonProps {
    children?: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    option?: string;
    size?: string;
    className?: string;
}

interface DropdownOption {
    id: number;
    name: string;
}

interface DropdownProps {
    label?: ReactNode;
    options: DropdownOption[];
    value?: number | string;
    required?: boolean;
    error?: ReactNode;
    onChange: (value: string) => void;
}

interface EditorProps {
    label?: ReactNode;
    required?: boolean;
    error?: ReactNode;
    readOnly?: boolean;
    defaultValue?: string | null;
    onChange: (html: string) => void;
}

const Button = ButtonBase as unknown as ComponentType<ButtonProps>;
const Dropdown = DropdownBase as unknown as ComponentType<DropdownProps>;
const Editor = EditorBase as unknown as ComponentType<EditorProps>;

// An emptied Quill editor still emits a bare block element, which must not be stored as a summary.
const isBlankHtml = (html: string) =>
    html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim() === '';

const ReleaseNewsEditContainer = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const { isModalOpen, openModal, closeModal } = useModal();
    const form = useSelector((state: RootState) => state.CommonReducer.forms?.releaseNewsForm);
    const submitting = useSelector((state: RootState) => state.CommonReducer.dataLoading?.releaseNews ?? false);

    useEffect(() => {
        return () => {
            dispatch(commonActions.cancelFormEditing(forms.releaseNewsForm));
        };
    }, [dispatch]);

    if (!form?.data) {
        return null;
    }

    const data = form.data;
    const isNew = id === NEW_RELEASE_NEWS_ID;
    const titleEmpty = (data.title ?? '').trim() === '';
    const editorKey = data.id ?? NEW_RELEASE_NEWS_ID;

    const severityOptions: DropdownOption[] = [
        { id: ReleaseSeverity.Minor, name: t('releasenews.minor') ?? '' },
        { id: ReleaseSeverity.Major, name: t('releasenews.major') ?? '' },
    ];

    const navigateToList = () => navigate(routes.releaseNews);

    const onSaveClick = () => {
        const save = isNew ? releaseNewsActions.create(data) : releaseNewsActions.update(data);
        void dispatch(save).then((saved) => {
            if (saved) void navigateToList();
        });
    };

    const onCancelClick = () => {
        dispatch(commonActions.cancelFormEditing(form.id, navigateToList));
    };

    const onDeleteConfirmed = () => {
        closeModal();
        if (data.id !== undefined) {
            void dispatch(releaseNewsActions.remove(data.id)).then((deleted) => {
                if (deleted) void navigateToList();
            });
        }
    };

    const onFieldChange = (field: keyof ReleaseNewsItem, value: unknown) => {
        dispatch(commonActions.changeMultiFormField(form.id, field, value));
    };

    return (
        <>
            <Card>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <Card.Header.Title>{isNew ? t('releasenews.create') : t('releasenews.edit')}</Card.Header.Title>
                        {!isNew && (
                            <SkynetIconButton icon="delete" label={t('releasenews.delete') ?? ''} disabled={submitting} onClick={() => openModal(Modals.ConfirmDeleteModal)} />
                        )}
                    </div>
                </Card.Header>
                <Card.Content>
                    <form>
                        <fieldset className="d-grid gap-16">
                            <FormInput required label={t('releasenews.title')} value={data.title} onChange={(value: string) => onFieldChange('title', value)} />
                            <DatePicker label={t('releasenews.published')} showTime={false} value={data.publishDate} onChange={(value) => onFieldChange('publishDate', value)} />
                            <Dropdown
                                label={t('releasenews.severity')}
                                options={severityOptions}
                                value={data.severity ?? ReleaseSeverity.Minor}
                                onChange={(value) => onFieldChange('severity', Number(value))}
                            />
                            <Editor
                                key={`summary-${editorKey}`}
                                readOnly={false}
                                label={t('releasenews.summary')}
                                defaultValue={data.summary}
                                onChange={(html) => onFieldChange('summary', isBlankHtml(html) ? '' : html)}
                            />
                            <Editor
                                key={`content-${editorKey}`}
                                readOnly={false}
                                label={t('releasenews.content')}
                                defaultValue={data.content}
                                onChange={(html) => onFieldChange('content', html)}
                            />
                        </fieldset>
                    </form>
                </Card.Content>
                <Card.Footer>
                    <Card.Footer.Buttons>
                        {submitting && <Spinner size={SpinnerSize.SMALL} position={SpinnerPosition.LEFT} />}
                        <Button onClick={onCancelClick}>{t('releasenews.cancel')}</Button>
                        <Button option={ButtonOption.PRIMARY} disabled={titleEmpty || submitting} onClick={onSaveClick}>
                            {t('releasenews.save')}
                        </Button>
                    </Card.Footer.Buttons>
                </Card.Footer>
            </Card>
            {isModalOpen(Modals.ConfirmDeleteModal) && <ConfirmDeleteModal itemName={data.title ?? ''} onSubmit={onDeleteConfirmed} />}
        </>
    );
};

export default ReleaseNewsEditContainer;
